import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

/**
 * Phoenix Capital toast primitive — non-blocking, auto-dismiss, stackable.
 * Lives at the app root via <ToastProvider>, exposes useToast() returning
 * a function that queues a toast. Pure (no auth / router), so it is the
 * single shared toast surface for BOTH the staff app and the external
 * portals, imported from the design-system barrel.
 *
 * Every meaningful portal action (saved / uploaded / submitted / withdrawn /
 * returned) fires one of these; pass `action` for a reversible "Undo".
 */

export type ToastTone = "success" | "error" | "info";

export interface ToastOptions {
  message: string;
  tone?: ToastTone;
  /** Default 4000ms. Pass 0 to disable auto-dismiss. */
  durationMs?: number;
  /** Optional inline action shown after the message (e.g. Undo). */
  action?: { label: string; onClick: () => void };
}

interface ToastEntry extends ToastOptions {
  id: number;
  tone: ToastTone;
  durationMs: number;
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => void;
  success: (message: string, opts?: Omit<ToastOptions, "message" | "tone">) => void;
  error: (message: string, opts?: Omit<ToastOptions, "message" | "tone">) => void;
  info: (message: string, opts?: Omit<ToastOptions, "message" | "tone">) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

const DEFAULT_DURATION = 4000;

// Refuse to render a toast with no body — without this a caller that
// accidentally passes "" produces a tone icon with no text, leaving the
// user no idea what happened. Tone-specific fallbacks keep feedback present.
function safeMessage(message: string, fallback: string): string {
  return typeof message === "string" && message.trim().length > 0
    ? message
    : fallback;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((opts: ToastOptions) => {
    const id = nextId.current++;
    const entry: ToastEntry = {
      id,
      tone: opts.tone ?? "info",
      durationMs: opts.durationMs ?? DEFAULT_DURATION,
      ...opts,
    };
    setItems((prev) => [...prev, entry]);
  }, []);

  const success = useCallback(
    (message: string, opts: Omit<ToastOptions, "message" | "tone"> = {}) =>
      toast({ ...opts, message: safeMessage(message, "Done."), tone: "success" }),
    [toast],
  );
  const error = useCallback(
    (message: string, opts: Omit<ToastOptions, "message" | "tone"> = {}) =>
      toast({
        ...opts,
        message: safeMessage(message, "Something went wrong. Please try again."),
        tone: "error",
      }),
    [toast],
  );
  const info = useCallback(
    (message: string, opts: Omit<ToastOptions, "message" | "tone"> = {}) => {
      if (typeof message !== "string" || message.trim().length === 0) return;
      toast({ ...opts, message, tone: "info" });
    },
    [toast],
  );

  return (
    <ToastContext.Provider value={{ toast, success, error, info }}>
      {children}
      <Toaster items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function Toaster({
  items,
  onDismiss,
}: {
  items: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      className="fixed bottom-space-4 right-space-4 z-[60] flex flex-col items-end gap-space-2 max-w-[calc(100vw-2rem)]"
      role="region"
      aria-label="Notifications"
    >
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

const TONE_STYLES: Record<ToastTone, { ring: string; icon: ReactNode }> = {
  success: {
    ring: "border-status-success",
    icon: <CheckCircle2 size={16} className="text-status-success" aria-hidden />,
  },
  error: {
    ring: "border-status-error",
    icon: <AlertCircle size={16} className="text-status-error" aria-hidden />,
  },
  info: {
    ring: "border-gold",
    icon: <Info size={16} className="text-gold" aria-hidden />,
  },
};

function ToastCard({
  item,
  onDismiss,
}: {
  item: ToastEntry;
  onDismiss: () => void;
}) {
  useEffect(() => {
    if (item.durationMs <= 0) return;
    const handle = window.setTimeout(onDismiss, item.durationMs);
    return () => window.clearTimeout(handle);
  }, [item.durationMs, onDismiss]);

  const { ring, icon } = TONE_STYLES[item.tone];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-start gap-space-2 px-space-3 py-space-2 bg-white-pure border-l-[3px] ${ring} shadow-md rounded-md min-w-[280px] max-w-[420px] motion-safe:animate-slide-in-right`}
    >
      <div className="mt-[2px] shrink-0">{icon}</div>
      <div className="flex-1 text-small text-onyx">
        {item.message}
        {item.action && (
          <button
            type="button"
            onClick={() => {
              item.action!.onClick();
              onDismiss();
            }}
            className="ml-space-2 font-display text-label font-semibold uppercase tracking-label text-gold hover:text-gold-shadow"
          >
            {item.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-space-1 -m-space-1 rounded-full text-gray-mid hover:text-onyx hover:bg-white-warm"
        aria-label="Dismiss"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}
